import { useState, useEffect } from "react";
import { MainLayout } from "../components/Layout";
import { soldPhonesAPI, clientsAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Plus, Search, Edit, Trash2, Smartphone, ShieldCheck, ShieldAlert, ShieldX, Clock } from "lucide-react";
import { toast } from "sonner";

// Champs natifs (select) alignés sur le thème sombre
const inputCls =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring";

const ALERT_DAYS = 30; // seuil d'alerte fin de garantie

const EMPTY = {
  brand: "", model: "", imei: "", imei2: "", serial_number: "",
  color: "", storage: "", condition: "neuf",
  client_id: "", client_name: "", client_phone: "",
  sale_date: new Date().toISOString().slice(0, 10),
  sale_price: "", warranty_months: "12", supplier: "", notes: "",
};

function fmtDate(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return d; }
}

function daysLeft(end) {
  if (!end) return null;
  return Math.ceil((new Date(end).getTime() - Date.now()) / 86400000);
}

function WarrantyBadge({ phone }) {
  const base = "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold";
  if (!phone.warranty_months) {
    return <span className={`${base} bg-muted text-muted-foreground`}><ShieldX className="h-3 w-3" /> Sans garantie</span>;
  }
  const dl = daysLeft(phone.warranty_end);
  if (phone.warranty_active) {
    if (dl !== null && dl <= ALERT_DAYS) {
      return <span className={`${base} bg-amber-500/15 text-amber-400`}><Clock className="h-3 w-3" /> Expire dans {dl} j · {fmtDate(phone.warranty_end)}</span>;
    }
    return <span className={`${base} bg-green-500/15 text-green-400`}><ShieldCheck className="h-3 w-3" /> Sous garantie · {fmtDate(phone.warranty_end)}</span>;
  }
  return <span className={`${base} bg-red-500/15 text-red-400`}><ShieldAlert className="h-3 w-3" /> Expirée · {fmtDate(phone.warranty_end)}</span>;
}

export default function FlottePage() {
  const [phones, setPhones] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ ...EMPTY });

  const fetchPhones = async (q) => {
    try {
      const res = await soldPhonesAPI.list(q);
      setPhones(res.data || []);
    } catch (err) {
      toast.error("Erreur lors du chargement de la flotte");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPhones();
    clientsAPI.list().then((r) => setClients(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchPhones(searchQuery), 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Alertes : garanties qui expirent dans <= 30 j (encore actives)
  const expiringSoon = phones
    .filter((p) => p.warranty_active && daysLeft(p.warranty_end) !== null && daysLeft(p.warranty_end) <= ALERT_DAYS)
    .sort((a, b) => daysLeft(a.warranty_end) - daysLeft(b.warranty_end));

  const openCreate = () => { setEditing(null); setFormData({ ...EMPTY }); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setFormData({
      brand: p.brand || "", model: p.model || "", imei: p.imei || "", imei2: p.imei2 || "",
      serial_number: p.serial_number || "", color: p.color || "", storage: p.storage || "",
      condition: p.condition || "neuf", client_id: p.client_id || "", client_name: p.client_name || "",
      client_phone: p.client_phone || "", sale_date: (p.sale_date || "").slice(0, 10),
      sale_price: p.sale_price ?? "", warranty_months: p.warranty_months ?? "", supplier: p.supplier || "", notes: p.notes || "",
    });
    setDialogOpen(true);
  };

  const onClientSelect = (id) => {
    const c = clients.find((x) => x.id === id);
    setFormData((f) => ({
      ...f,
      client_id: id,
      client_name: c ? (c.name || `${c.first_name || ""} ${c.last_name || ""}`.trim()) : f.client_name,
      client_phone: c ? (c.phone || "") : f.client_phone,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.brand.trim() || !formData.model.trim()) { toast.error("Marque et modèle obligatoires"); return; }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        client_id: formData.client_id || null,
        sale_price: formData.sale_price === "" ? 0 : Number(formData.sale_price),
        warranty_months: formData.warranty_months === "" ? 0 : parseInt(formData.warranty_months) || 0,
      };
      if (editing) {
        await soldPhonesAPI.update(editing.id, payload);
        toast.success("Téléphone mis à jour");
      } else {
        await soldPhonesAPI.create(payload);
        toast.success("Téléphone ajouté à la flotte");
      }
      setDialogOpen(false);
      fetchPhones(searchQuery);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Supprimer ${p.brand} ${p.model} de la flotte ?`)) return;
    try {
      await soldPhonesAPI.delete(p.id);
      toast.success("Téléphone supprimé");
      fetchPhones(searchQuery);
    } catch {
      toast.error("Suppression impossible");
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
              <Smartphone className="h-6 w-6 text-primary" /> Flotte — Téléphones vendus
            </h1>
            <p className="text-sm text-muted-foreground">Suivi des appareils vendus et de leur garantie (IMEI, n° de série, client).</p>
          </div>
          <Button onClick={openCreate}><Plus className="mr-2 h-4 w-4" /> Ajouter un téléphone</Button>
        </div>

        {/* Alertes fin de garantie */}
        {expiringSoon.length > 0 && (
          <Card className="border-amber-500/40 bg-amber-500/5">
            <CardContent className="p-4">
              <div className="mb-2 flex items-center gap-2 font-semibold text-amber-400">
                <Clock className="h-4 w-4" /> {expiringSoon.length} garantie{expiringSoon.length > 1 ? "s" : ""} expire{expiringSoon.length > 1 ? "nt" : ""} bientôt (≤ {ALERT_DAYS} j)
              </div>
              <div className="space-y-1">
                {expiringSoon.slice(0, 8).map((p) => (
                  <button key={p.id} onClick={() => openEdit(p)} className="flex w-full items-center justify-between rounded px-2 py-1 text-left text-sm hover:bg-muted/40">
                    <span className="text-foreground">{p.brand} {p.model} — <span className="text-muted-foreground">{p.client_name || "client ?"}</span></span>
                    <span className="font-medium text-amber-400">expire dans {daysLeft(p.warranty_end)} j ({fmtDate(p.warranty_end)})</span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Rechercher (IMEI, n° série, marque, modèle, client…)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {loading ? (
          <p className="py-10 text-center text-muted-foreground">Chargement…</p>
        ) : phones.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-muted-foreground">
            <Smartphone className="mx-auto mb-3 h-10 w-10 opacity-40" />
            Aucun téléphone enregistré. Cliquez sur « Ajouter un téléphone » après chaque vente.
          </CardContent></Card>
        ) : (
          <div className="grid gap-3">
            {phones.map((p) => (
              <Card key={p.id}>
                <CardContent className="flex flex-wrap items-center gap-4 p-4">
                  <div className="min-w-[180px] flex-1">
                    <div className="font-semibold text-foreground">{p.brand} {p.model} {p.storage ? `· ${p.storage}` : ""} {p.color ? `· ${p.color}` : ""}</div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {p.imei ? `IMEI ${p.imei}` : ""}{p.imei2 ? ` / ${p.imei2}` : ""}{p.serial_number ? `  ·  SN ${p.serial_number}` : ""}
                    </div>
                    <div className="mt-0.5 text-xs text-muted-foreground">{p.condition || ""}{p.supplier ? `  ·  ${p.supplier}` : ""}</div>
                  </div>
                  <div className="min-w-[140px]">
                    <div className="text-sm font-medium text-foreground">{p.client_name || "—"}</div>
                    <div className="text-xs text-muted-foreground">{p.client_phone || ""}</div>
                  </div>
                  <div className="min-w-[120px] text-sm text-foreground">
                    <div>Vendu : {fmtDate(p.sale_date)}</div>
                    {p.sale_price ? <div className="text-xs text-muted-foreground">{Number(p.sale_price).toFixed(2)} €</div> : null}
                  </div>
                  <div className="min-w-[180px]"><WarrantyBadge phone={p} /></div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(p)}><Edit className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(p)}><Trash2 className="h-4 w-4 text-red-400" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[640px]">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifier le téléphone" : "Nouveau téléphone vendu"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Marque *</Label><Input value={formData.brand} onChange={(e) => setFormData({ ...formData, brand: e.target.value })} placeholder="Apple, Samsung…" /></div>
              <div><Label>Modèle *</Label><Input value={formData.model} onChange={(e) => setFormData({ ...formData, model: e.target.value })} placeholder="iPhone 13, Galaxy S22…" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>IMEI</Label><Input value={formData.imei} onChange={(e) => setFormData({ ...formData, imei: e.target.value })} placeholder="15 chiffres" /></div>
              <div><Label>IMEI 2 (dual SIM)</Label><Input value={formData.imei2} onChange={(e) => setFormData({ ...formData, imei2: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>N° de série (SN)</Label><Input value={formData.serial_number} onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })} /></div>
              <div><Label>État</Label>
                <select className={inputCls} value={formData.condition} onChange={(e) => setFormData({ ...formData, condition: e.target.value })}>
                  <option value="neuf">Neuf</option>
                  <option value="reconditionné">Reconditionné</option>
                  <option value="occasion">Occasion</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Couleur</Label><Input value={formData.color} onChange={(e) => setFormData({ ...formData, color: e.target.value })} /></div>
              <div><Label>Stockage</Label><Input value={formData.storage} onChange={(e) => setFormData({ ...formData, storage: e.target.value })} placeholder="128 Go…" /></div>
            </div>

            <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-3">
              <Label className="text-xs font-bold uppercase text-muted-foreground">Client</Label>
              <select className={inputCls} value={formData.client_id} onChange={(e) => onClientSelect(e.target.value)}>
                <option value="">— Client non enregistré (saisie libre) —</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.name || `${c.first_name || ""} ${c.last_name || ""}`.trim()} {c.phone ? `(${c.phone})` : ""}</option>
                ))}
              </select>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nom client</Label><Input value={formData.client_name} onChange={(e) => setFormData({ ...formData, client_name: e.target.value })} /></div>
                <div><Label>Téléphone client</Label><Input value={formData.client_phone} onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })} /></div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div><Label>Date de vente</Label><Input type="date" value={formData.sale_date} onChange={(e) => setFormData({ ...formData, sale_date: e.target.value })} /></div>
              <div><Label>Prix (€)</Label><Input type="number" step="0.01" value={formData.sale_price} onChange={(e) => setFormData({ ...formData, sale_price: e.target.value })} /></div>
              <div><Label>Garantie (mois)</Label><Input type="number" value={formData.warranty_months} onChange={(e) => setFormData({ ...formData, warranty_months: e.target.value })} placeholder="12" /></div>
            </div>
            <div><Label>Fournisseur / origine</Label><Input value={formData.supplier} onChange={(e) => setFormData({ ...formData, supplier: e.target.value })} /></div>
            <div><Label>Notes</Label><Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} /></div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={saving}>{saving ? "Enregistrement…" : editing ? "Enregistrer" : "Ajouter"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
